import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef } from 'react';
import styles from './AccordionItem.module.scss';
import {
  setupMouseEnterTimeline,
  setupMouseLeaveTimeline,
  setupTransitionInTimeline,
  toggle,
  toggleIconState,
} from './AccordionItem.transitions';
import type { AccordionItemProps, AccordionItemRef } from './AccordionItem.data';
import { useScrollTransition, useTransitionController } from 'util/react-transition-component';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import useMount from '../../../../hooks/useMount';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';
import { useIsDesktopDevice } from '../../../../hooks/useIsDesktopDevice';
import { useTimeout } from '../../../../hooks/useTimeout';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import PrimaryButton from 'components/atoms/primary-button/PrimaryButton';
import { useGlobalStore } from '@/stores/global';

export default function AccordionItem({
  isOpen,
  onClick,
  index,
  blok,
  ref,
  ...props
}: AccordionItemProps & { ref?: Ref<AccordionItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const contentRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const verticalLineRef = useRef<HTMLSpanElement>(null);
  const headingRef = useRef<HeadingRef>(null);
  const descriptionRef = useRef<TextRef>(null);
  const isDesktopDevice = useIsDesktopDevice(false);
  const lineRef = useRef<HTMLSpanElement>(null);

  const globalStore = useGlobalStore();

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        headingRef,
        descriptionRef,
        lineRef,
        iconRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const { start: refreshScrollTriggerTimeout } = useTimeout(
    () => {
      ScrollTrigger.refresh();
    },
    0,
    false,
  );

  useUpdateEffect(() => {
    if (contentRef.current) {
      toggle(contentRef, isOpen, refreshScrollTriggerTimeout, false);
    }
  }, [isOpen]);

  useMount(() => {
    if (!isOpen && contentRef.current) {
      toggle(contentRef, isOpen, refreshScrollTriggerTimeout, true);
    }
  });

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        headingRef,
        descriptionRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  useUpdateEffect(() => {
    if (isOpen) {
      hoverTransitionController.transitionOut();
    }
    toggleIconState(
      {
        iconRef,
        verticalLineRef,
      },
      isOpen,
    );
  }, [isOpen]);

  const onMouseEnter = useCallback(() => {
    if (!isOpen && isDesktopDevice) {
      hoverTransitionController.transitionIn();
    }
  }, [hoverTransitionController, isOpen, isDesktopDevice]);

  const onMouseLeave = useCallback(() => {
    if (!isOpen && isDesktopDevice) {
      hoverTransitionController.transitionOut();
    }
  }, [hoverTransitionController, isOpen, isDesktopDevice]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bulletList = blok.bullet_list?.content[0] as any;
  const isExpandable =
    blok.image?.filename || bulletList?.content?.length > 0 || blok.long_description;

  return (
    <div
      ref={elementRef}
      className={classNames(styles.accordionItem, props.className)}
      {...sbEditable(blok)}
    >
      <button
        type="button"
        className={classNames(styles.header, 'button', {
          [styles.disabled]: !isExpandable,
        })}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className={classNames(styles.headerContent)}>
          <Heading
            ref={headingRef}
            className={classNames(styles.title)}
            as="h4"
            size="heading-05"
            transitionInType="splitRotateX3dIn"
          >
            {index !== undefined && (
              <span className={styles.index}>{`0${index + 1}`.slice(-2)}</span>
            )}
            {blok.title}
          </Heading>

          <Text
            ref={descriptionRef}
            className={styles.description}
            as="p"
            size="body-standard"
            transitionInType={isDesktopDevice ? 'splitCharsSlideInUp' : 'slideUpFadeIn'}
          >
            {blok.short_description}
          </Text>
        </div>

        {isExpandable && (
          <div ref={iconRef} className={classNames(styles.icon)}>
            <span className={classNames(styles.line)} />
            <span ref={verticalLineRef} className={classNames(styles.line)} />
          </div>
        )}
      </button>

      <div className={styles.content} ref={contentRef}>
        {/* Temporarily hidden until content has been approved */}

        {/*{blok.image?.filename && (*/}
        {/*  <div className={styles.imageContainer}>*/}
        {/*    <Image*/}
        {/*      className={styles.image}*/}
        {/*      src={blok.image?.filename}*/}
        {/*      alt={blok.image.alt || ''}*/}
        {/*      fill*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*)}*/}

        <div className={styles.ctaContainer}>
          {blok.long_description && (
            <Text
              className={styles.longDescription}
              as={'p'}
              size={'body-standard'}
              disableTriggerInOnScroll={false}
            >
              {blok.long_description}
            </Text>
          )}

          {blok?.link?.cached_url && (
            <PrimaryButton
              label={globalStore.globalSettings.view_service}
              disableTriggerInOnScroll={false}
              link={{ href: blok.link.cached_url }}
              className={styles.cta}
            />
          )}
        </div>

        {bulletList && bulletList?.content?.length > 0 && (
          <ul ref={listRef} className={classNames(styles.list)}>
            {render(blok.bullet_list, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children) => (
                  <li className={classNames(styles.listItem, 'grid-gutter')}>
                    <Heading
                      className={classNames(styles.listTitle)}
                      as="h5"
                      size="heading-05"
                      transitionInType="none"
                    >
                      {children}
                    </Heading>
                  </li>
                ),
              },
            })}
          </ul>
        )}
      </div>

      <span ref={lineRef} className={styles.listLine} />
    </div>
  );
}
